import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import axiosInstance from '../../../Config/axiosInstance';
import AddPricePerHour from './AddPricePerHour';
import EditPricePerHour from './EditPricePerHour';
import './PricePerHour.css';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

function PricePerHour() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const [rows, setRows] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const formatDateToRomanian = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';

    const n = Number(value);
    if (Number.isNaN(n)) return '';

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  useEffect(() => {
    const requiredPath = "/price-per-hour";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    setCanCreate(
      permissions.some(
        (page) =>
          new URL(page.url).pathname === requiredPath &&
          page.can_create
      )
    );

    setCanEdit(
      permissions.some(
        (page) =>
          new URL(page.url).pathname === requiredPath &&
          page.can_edit
      )
    );

    setCanDelete(
      permissions.some(
        (page) =>
          new URL(page.url).pathname === requiredPath &&
          page.can_delete
      )
    );
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          priceResponse,
          resourceResponse,
          currenciesResponse,
          exchangeRatesResponse,
          systemCurrencyResponse,
        ] = await Promise.all([
          axiosInstance.get('/price-per-hour/'),
          axiosInstance.get('/internal-resources/'),
          axiosInstance.get('/currencies/'),
          axiosInstance.get('/exchange-rate/'),
          axiosInstance.get('/system-currency/'),
        ]);

        const resourceMap = (resourceResponse.data || []).reduce((acc, resource) => {
          acc[resource.id] = resource.resource_name;
          return acc;
        }, {});

        const currenciesMap = (currenciesResponse.data || []).reduce((acc, item) => {
          acc[item.id] = item.abbreviation || '';
          return acc;
        }, {});

        const systemCurrencyId =
          Array.isArray(systemCurrencyResponse.data) &&
            systemCurrencyResponse.data.length > 0
            ? systemCurrencyResponse.data[0]?.currency
            : null;

        const systemCurrencyLabel = currenciesMap[systemCurrencyId] || '';

        const exchangeRates = exchangeRatesResponse.data || [];

        const getExchangeRateForItem = (item) => {
          if (!item.currency || !systemCurrencyId) return null;

          if (Number(item.currency) === Number(systemCurrencyId)) {
            return null;
          }

          if (item.exchange_rate) {
            return exchangeRates.find(
              rate => Number(rate.id) === Number(item.exchange_rate)
            ) || null;
          }

          return exchangeRates
            .filter(rate => Number(rate.currency) === Number(item.currency))
            .sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null;
        };

        const computeSystemPrice = (item) => {
          const price = Number(item.price_per_hour);

          if (!Number.isFinite(price)) return '';

          if (!item.currency || !systemCurrencyId) return '';

          if (Number(item.currency) === Number(systemCurrencyId)) {
            return price;
          }

          const exchangeRate = getExchangeRateForItem(item);
          const rateValue = Number(exchangeRate?.value);

          if (!Number.isFinite(rateValue) || rateValue <= 0) return '';

          return price * rateValue;
        };

        const sortedData = (priceResponse.data || []).map((item) => {
          const currencyAbbreviation = currenciesMap[item.currency] || '';
          const systemPrice = computeSystemPrice(item);

          return {
            id: item.id,
            price_per_hour_value: item.price_per_hour ?? 0,
            currency: currencyAbbreviation,
            price_per_hour_system_value: systemPrice,
            price_per_hour_system_currency: systemCurrencyLabel,
            internal_resource: resourceMap[item.internal_resource] || "-",
            date: item.date,
          };
        }).sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();
  }, [location.pathname]);

  const handleAddClick = () => navigate('/price-per-hour/add-price_per_hour');
  const handleEditClick = (row) => navigate(`/price-per-hour/edit-price_per_hour/${row.id}`);

  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  const columns = [
    {
      field: 'price_per_hour_value',
      headerName: 'Price/Hour',
      flex: 1,
      minWidth: 140,
      sortComparator: (v1, v2) => Number(v1) - Number(v2),
      renderCell: (params) => {
        const value = formatNumber(params.row.price_per_hour_value);
        const currency = params.row.currency;

        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {`${value}${currency ? ` ${currency}` : ''}`}
          </div>
        );
      }
    },
    {
      field: 'price_per_hour_system_value',
      headerName: 'Price/Hour System',
      flex: 1,
      minWidth: 160,
      sortComparator: (v1, v2) => Number(v1 || 0) - Number(v2 || 0),
      renderCell: (params) => {
        const value = params.row.price_per_hour_system_value;
        const currency = params.row.price_per_hour_system_currency;

        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {value !== '' && value !== null && value !== undefined
              ? `${formatNumber(value)}${currency ? ` ${currency}` : ''}`
              : '-'}
          </div>
        );
      }
    },
    {
      field: 'internal_resource',
      headerName: 'Internal Resource',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatDateToRomanian(params.value)}
        </div>
      ),
      sortComparator: (v1, v2) => {
        const date1 = new Date(v1);
        const date2 = new Date(v2);
        return date1.getTime() - date2.getTime();
      }
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}

          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="price-per-hour-page">
            <div className="price-header-container">
              <h2 className='title'>PRICE / HOUR</h2>

              {canCreate && (
                <div className="add-button-container">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ float: 'right', marginBottom: '40px' }}
                  >
                    {isSmallScreen ? 'Add' : 'Add Price/Hour'}
                  </Button>
                </div>
              )}
            </div>

            <div className="table-container">
              <DataGrid
                rows={rows}
                columns={columns}
                getRowHeight={() => "auto"}
                pageSize={5}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "id", sort: "desc" }],
                  },
                  pagination: {
                    paginationModel: { pageSize: 11 },
                  },
                }}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    whiteSpace: "normal",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "transparent !important",
                  },
                }}
                pageSizeOptions={[10, 11, 20, 25]}
              />
            </div>

            <Dialog
              open={openDeleteDialog}
              onClose={() => setOpenDeleteDialog(false)}
            >
              <DialogTitle>Confirm Delete</DialogTitle>

              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete this record?
                </DialogContentText>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={() => setOpenDeleteDialog(false)}
                  sx={{
                    backgroundColor: '#e0e0e0',
                    color: '#424242',
                    '&:hover': { backgroundColor: '#bdbdbd' }
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={async () => {
                    try {
                      await axiosInstance.delete(`/price-per-hour/${itemToDelete.id}/`);
                      setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
                      setOpenDeleteDialog(false);
                    } catch (error) {
                      const message =
                        error.response?.data?.error ||
                        error.response?.data?.detail ||
                        "An unexpected error occurred while deleting.";

                      setDeleteErrorMessage(message);
                      setOpenDeleteDialog(false);
                      setErrorDialogOpen(true);
                    }
                  }}
                  sx={{
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    '&:hover': { backgroundColor: '#b71c1c' }
                  }}
                  autoFocus
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={errorDialogOpen}
              onClose={() => setErrorDialogOpen(false)}
            >
              <DialogTitle style={{ color: 'red' }}>
                Delete Failed
              </DialogTitle>

              <DialogContent>
                <DialogContentText>
                  {deleteErrorMessage}
                </DialogContentText>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={() => setErrorDialogOpen(false)}
                  sx={{
                    backgroundColor: '#e0e0e0',
                    color: '#424242',
                    '&:hover': { backgroundColor: '#bdbdbd' }
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        }
      />

      <Route path="add-price_per_hour" element={<AddPricePerHour />} />
      <Route path="edit-price_per_hour/:id" element={<EditPricePerHour />} />
    </Routes>
  );
}

export default PricePerHour;